import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from 'src/lib/utils';

const ToastProvider = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Provider>) => (
  <ToastPrimitives.Provider swipeDirection="right" {...props}>
    {children}
  </ToastPrimitives.Provider>
);

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-[9999] tw-flex tw-max-h-screen tw-w-full tw-flex-col-reverse tw-p-4 sm:tw-bottom-0 sm:tw-right-0 sm:tw-top-auto sm:tw-flex-col md:tw-left-auto md:tw-max-w-[420px]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'tw-group tw-pointer-events-auto tw-relative tw-flex tw-min-h-14 tw-w-full tw-items-center tw-justify-between tw-space-x-4 tw-overflow-hidden tw-rounded-lg tw-border tw-bg-neutral-800 tw-px-2.5 tw-py-4 tw-text-white tw-shadow-lg tw-transition-all data-[swipe=cancel]:tw-translate-x-0 data-[swipe=end]:tw-translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:tw-translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:tw-transition-none data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[swipe=end]:tw-animate-out data-[state=closed]:tw-fade-out-80 data-[state=closed]:tw-slide-out-to-right-full data-[state=open]:tw-slide-in-from-top-full data-[state=open]:sm:tw-slide-in-from-bottom-full',
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, onLostPointerCapture, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants(), className)}
      style={{
        // Radix Toast wraps each Root in a DismissableLayer which sets
        // pointer-events: none inline whenever a higher modal layer (e.g. a
        // Dialog with modal=true) has disableOutsidePointerEvents. That
        // inline style beats the Tailwind tw-pointer-events-auto class and
        // kills the swipe gesture. Re-asserting auto here restores it.
        pointerEvents: 'auto',
        boxShadow: '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
      }}
      // Radix resolves a swipe (dismiss vs snap-back) ONLY inside its own
      // onPointerUp handler. If pointer capture is lost mid-swipe without a
      // pointerup reaching that handler — cursor leaves the window, the gesture
      // is cancelled, a higher modal layer steals the pointer, the auto-dismiss
      // timer fires, or the toast re-renders — the swipeDeltaRef never resets
      // and the toast freezes with `data-swipe="move"` and the translate CSS
      // var still applied (#2435).
      //
      // lostpointercapture fires on ANY capture loss (a strict superset of
      // pointercancel), so synthesize a pointerup and let Radix's existing
      // cleanup resolve the gesture by the last recorded delta. Guard on
      // data-swipe still being mid-gesture so a normal pointerup (which also
      // releases capture → fires lostpointercapture) isn't double-handled; even
      // if it were, Radix's delta is already null by then, so it's a no-op.
      onLostPointerCapture={(event) => {
        const swipeState = event.currentTarget.getAttribute('data-swipe');
        if (swipeState === 'move' || swipeState === 'start') {
          event.currentTarget.dispatchEvent(
            new PointerEvent('pointerup', {
              bubbles: true,
              pointerId: event.pointerId,
              pointerType: event.pointerType,
              clientX: event.clientX,
              clientY: event.clientY,
              button: 0,
            }),
          );
        }
        onLostPointerCapture?.(event);
      }}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'tw-inline-flex tw-h-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border tw-bg-transparent tw-px-3 tw-font-medium tw-ring-offset-background tw-transition-colors hover:tw-bg-secondary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50 group-[.destructive]:tw-border-muted/40 group-[.destructive]:hover:tw-border-destructive/30 group-[.destructive]:hover:tw-bg-destructive group-[.destructive]:hover:tw-text-destructive-foreground group-[.destructive]:focus:tw-ring-destructive',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-rounded-md tw-p-1 tw-text-white/60 tw-opacity-100 tw-transition-opacity hover:tw-bg-white/10 hover:tw-text-white focus:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-white/20 group-[.destructive]:tw-text-red-300 group-[.destructive]:hover:tw-bg-red-500/20 group-[.destructive]:hover:tw-text-red-50 group-[.destructive]:focus:tw-ring-red-400',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="tw-h-4 tw-w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('tw-font-semibold', className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn('tw-break-words tw-font-medium', className)} {...props} />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
